import { useEffect, useState } from 'react';
import { getReferenceString, isPopulated, isReference, normalizeOperationOutcome } from '@medplum/core';
import type { OperationOutcome, Reference, Resource } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';

type UseResourceValue<T extends Resource> = Reference<T> | Partial<T> | T | undefined;

function getInitialResource<T extends Resource>(value: UseResourceValue<T>): T | undefined {
  if (value == null) return undefined;
  if (isPopulated(value)) return value as T;
  return undefined;
}

/**
 * Resolves one or more references/resources into full resources.
 * Accepts either a single value or an array; returns T or T[] accordingly.
 * You cannot call useResource in a loop, so array input is handled in a single effect.
 */
export default function useResources<T extends Resource>(
  valueOrValues: UseResourceValue<T> | UseResourceValue<T>[],
  setOutcome?: (outcome: OperationOutcome) => void
): T | T[] | undefined {
  const medplum = useMedplum();
  const isArray = Array.isArray(valueOrValues);
  const values = isArray ? valueOrValues : [valueOrValues];

  const [single, setSingle] = useState<T | undefined>(() =>
    isArray ? undefined : getInitialResource(valueOrValues as UseResourceValue<T>)
  );
  const [multiple, setMultiple] = useState<T[] | undefined>(() =>
    isArray && values.length > 0 && values.every((v) => v != null && isPopulated(v))
      ? (values.filter((v): v is T => v != null && isPopulated(v)) as T[])
      : undefined
  );

  const valueKey = values
    .map((v) => (v == null ? '' : isPopulated(v) ? getReferenceString(v as T) : ((v as Reference<T>).reference ?? '')))
    .join(',');

  useEffect(() => {
    if (values.length === 0) {
      if (isArray) setMultiple([]);
      else setSingle(undefined);
      return;
    }

    let cancelled = false;

    const resolve = async () => {
      const results = await Promise.all(
        values.map(async (v): Promise<T | undefined> => {
          if (v == null) return undefined;
          if (isPopulated(v)) return v as T;
          if (!isReference(v)) return undefined;
          try {
            return (await medplum.readReference(v as Reference<T>)) as T;
          } catch (err) {
            if (!cancelled && setOutcome) {
              setOutcome(normalizeOperationOutcome(err));
            }
            return undefined;
          }
        })
      );
      const loaded = results.filter((r) => r != null) as T[];
      if (!cancelled) {
        if (isArray) setMultiple(loaded);
        else setSingle(loaded[0]);
      }
    };

    const hasRefs = values.some((v) => v != null && !isPopulated(v));
    if (hasRefs) {
      resolve();
    } else {
      const loaded = values.filter((v) => v != null && isPopulated(v)).map((v) => v as T);
      if (isArray) setMultiple(loaded);
      else setSingle(loaded[0]);
    }

    return () => {
      cancelled = true;
    };
  }, [medplum, valueKey, isArray, setOutcome]);

  return isArray ? multiple : single;
}
